/* eslint-disable no-nested-ternary */
import { useCallback } from "react";
import { logger } from "../../../utils/logger";
import currency from "currency.js";
import { SubmitHandler } from "react-hook-form";
import {
  MarginTypeResponse,
  OrderTypeResponse,
  SideResponse,
} from "../../../codegen-api";
import { COLORS } from "../../../constants/design/colors";
import { getDisplayInstrumentName } from "../../../utils/displayName";
import {
  alignPriceToStep,
  floorToStepString,
  roundToStepSize,
} from "../../../utils/format";
import { toPlainDecimalString } from "../../../utils/decimalString";
import { getTxExplorerUrl } from "../../../utils/chain";
import { getMarginTypeLabel, normalizeMarginType } from "../../../utils/margin";
import {
  ToastEnum,
  ToastStatusEnum,
} from "../../../utils/toast";
import { IPerpsFormFieldValues } from "./form";
import { sizingPriceForOrder } from "./orderSizingPrice";
import { usePerpsTradeFormSizing } from "./usePerpsTradeFormSizing";
import { useWrongNetworkHandler } from "../../../hooks/wallet/useWrongNetworkHandler";

export function usePerpsTradeFormSubmit(
  ctx: ReturnType<typeof usePerpsTradeFormSizing>
) {
  const {
    perpInstrument,
    orderType,
    orderDirection,
    calculateTotalValueWithSize,
    contractPriceStep,
    getRealContractSize,
    selectedLeverage,
    placePerpOrder,
    closePerpPosition,
    estimatedOpeningFee,
    openingFeeRate,
    addToast,
    addErrorToast,
    tradeUseUSDCTerms,
    reduceOnly,
    currentPosition,
    playSound,
    resetAmountInput,
    setIsLoading,
    tpsl,
    tpslRoeRef,
  } = ctx;

  const handleWrongNetwork = useWrongNetworkHandler();

  const submitOrder = useCallback<SubmitHandler<IPerpsFormFieldValues>>(
    async (values) => {
      if (!perpInstrument) return;

      // Calculate order value in USD
      const orderValueUSD = calculateTotalValueWithSize(values.amount);
      if (orderValueUSD <= 0) return;

      try {
        setIsLoading(true);

        // Limit price, else mark. Never the index: it is a different price.
        const priceForSize =
          sizingPriceForOrder({
            orderType,
            limitPrice: values.price,
            markPrice: perpInstrument.mark_price,
          }) ?? 0;
        const stepSize = contractPriceStep.amount_step;
        const precision = contractPriceStep.amount_precision;

        // A market order can be sized from the book without any price.
        const bookSizeInBase =
          tradeUseUSDCTerms && orderType === OrderTypeResponse.Market
            ? getRealContractSize(values.amount, true)
            : 0;

        if (tradeUseUSDCTerms && !bookSizeInBase && priceForSize <= 0) {
          addErrorToast(<p>Price Error</p>, "Cannot determine price for order sizing");
          return;
        }

        const rawSizeInBase = tradeUseUSDCTerms
          ? bookSizeInBase || orderValueUSD / priceForSize
          : Number(values.amount || 0);
        const roundedSize = roundToStepSize(rawSizeInBase, stepSize, precision);
        // What is sent: plain decimals at the step's precision. `String(n)`
        // wrote 0.0000005 as "5e-7", which parseUnits rejects.
        const sizeText = floorToStepString(rawSizeInBase, stepSize, precision);
        // The limit price is aligned here too, not only on blur: Enter and the
        // submit shortcut skip the blur.
        const limitPriceText =
          orderType === OrderTypeResponse.Limit
            ? alignPriceToStep(
                values.price,
                contractPriceStep.price_step,
                contractPriceStep.price_precision,
                orderDirection === SideResponse.Sell ? "up" : "down"
              )
            : undefined;

        if (roundedSize <= 0) {
          addErrorToast(
            <p>Order Size Error</p>,
            `Order size must be at least ${toPlainDecimalString(
              Math.max(stepSize, contractPriceStep.min_order_amount)
            )} ${perpInstrument.underlying_asset}`
          );
          return;
        }
        if (orderType === OrderTypeResponse.Limit && !(Number(limitPriceText) > 0)) {
          addErrorToast(<p>Price Error</p>, "Enter a valid limit price");
          return;
        }

        // TP/SL entered as ROE% is priced from this order's entry. A leg that
        // cannot be priced stops the order HERE, before anything is signed:
        // placing it and then reporting "TP/SL NOT set" would leave the trader
        // holding a position they believed was protected.
        const roeRef = {
          ...tpslRoeRef,
          entry: limitPriceText ?? tpslRoeRef.entry,
        };
        if (tpsl.enabled && !reduceOnly) {
          const tpslIssue = tpsl.resolve(roeRef).issue;
          if (tpslIssue) {
            addErrorToast(<p>TP/SL Error</p>, tpslIssue);
            return;
          }
        }

        // Collateral = orderValue / leverage
        const leverageForOrder = selectedLeverage;
        const collateral = orderValueUSD / leverageForOrder;
        // Logged only. Unknown fee rate (no verified schedule) stays unknown.
        const openingFee =
          openingFeeRate === undefined ? undefined : orderValueUSD * openingFeeRate;
        const requiredMargin =
          openingFee === undefined ? undefined : collateral + openingFee;
        const formMarginType =
          normalizeMarginType(values.marginType) || MarginTypeResponse.Cross;
        let orderMarginType = formMarginType;

        logger.debug("[PerpOrder] submitOrder", {
          market: perpInstrument.instrument_id,
          side: orderDirection,
          orderType,
          orderValueUSD,
          price: limitPriceText || "market",
          rawSizeInBase,
          sizeInBase: roundedSize,
          stepSize,
          collateral,
          openingFee,
          requiredMargin,
          leverage: leverageForOrder,
          marginType: getMarginTypeLabel(orderMarginType),
        });

        const isReduceOnlyMarket =
          orderType === OrderTypeResponse.Market && reduceOnly;
        let result: { order_id: string; tx_hash: string };

        if (isReduceOnlyMarket) {
          if (!currentPosition) {
            addErrorToast(<p>Reduce Only Error</p>, "No open position to reduce");
            return;
          }
          orderMarginType =
            normalizeMarginType(currentPosition.margin_type) ||
            formMarginType;
          if (currentPosition.side === orderDirection) {
            addErrorToast(
              <p>Reduce Only Error</p>,
              "Reduce-only orders must be opposite your current position"
            );
            return;
          }

          const currentPositionSize = Number(currentPosition.amount || 0);
          const sizeTolerance = Math.max(stepSize / 10, Number.EPSILON);
          if (roundedSize - currentPositionSize > sizeTolerance) {
            addErrorToast(
              <p>Reduce Only Error</p>,
              "Reduce-only size cannot exceed your current position"
            );
            return;
          }

          const closeSize =
            roundedSize >= currentPositionSize - sizeTolerance
              ? undefined
              : sizeText;

          result = await closePerpPosition(
            currentPosition,
            closeSize ? { size: closeSize } : undefined
          );
        } else {
          // Direct contract call to PerpEngine
          result = await placePerpOrder({
            market: perpInstrument.instrument_id,
            side: orderDirection,
            order_type: orderType,
            size: sizeText,
            price: limitPriceText,
            // Display only (margin is derived on-chain). Never rounded up.
            collateralAmount: floorToStepString(collateral, 0.01, 2),
            leverage: leverageForOrder,
            marginMode:
              orderMarginType === MarginTypeResponse.Isolated ? 0 : 1,
            // Pass reduce-only intent (e.g. a limit reduce-only order). The
            // contract enforces it once reduce-only is enabled on-chain.
            reduceOnly,
          });
        }

        if (result?.order_id) {
          playSound("order_placed", true);
          addToast(
            {
              type: ToastEnum.INFO,
              header: <p>{getDisplayInstrumentName(perpInstrument.instrument_name)} {orderDirection}</p>,
              subheader: (
                <span>
                  <span style={{ color: orderDirection === SideResponse.Buy ? COLORS.up.one : COLORS.down.one }}>
                    {orderType} {orderDirection}
                  </span>
                  {result.tx_hash && (
                    <>
                      {" · "}
                      <a href={getTxExplorerUrl(result.tx_hash)} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.link.one, textDecoration: "underline" }}>
                        View tx
                      </a>
                    </>
                  )}
                </span>
              ),
              stats: [
                { label: "Size", value: sizeText },
                { label: "Value", value: `$${orderValueUSD.toFixed(2)}` },
                { label: "Margin", value: getMarginTypeLabel(orderMarginType) },
                ...(estimatedOpeningFee !== undefined
                  ? [{ label: "Est. Fee", value: currency(estimatedOpeningFee).format() }]
                  : []),
              ],
              status: ToastStatusEnum.SUCCESS,
            },
            5000
          );
          resetAmountInput();
        }

        // Attach any requested TP/SL to the position this order just opened.
        //
        // Runs AFTER the success toast on purpose: the order is already on-chain, so
        // the fill must be confirmed to the user immediately rather than waiting on an
        // unrelated trigger POST — and if that POST fails, its warning should land
        // after the confirmation, not before it.
        //
        // Skipped for reduce-only orders: those shrink or close a position rather than
        // open one, so triggers on them are meaningless. Never throws — a failing
        // trigger POST must not be reported as a failed order (see useOrderFormTpsl),
        // which is also why this sits outside the toast block but inside the try.
        if (result?.order_id && !reduceOnly) {
          await tpsl.attachToOrder({
            instrumentName: perpInstrument.instrument_name,
            instrumentId: perpInstrument.instrument_id,
            side: orderDirection,
            amount: roundedSize,
            // The mode the order was actually submitted with, not re-derived.
            marginType: orderMarginType,
            roeRef,
          });
        }
      } catch (e: any) {
        console.error("[PerpOrder] Error:", e);
        // A wallet on the wrong network is something the user can fix in one
        // click, so it gets a dialog with a Switch button rather than a toast
        // that only states the problem. Everything else reports as before.
        if (!handleWrongNetwork(e)) {
          addErrorToast(
            <p>Order Failed</p>,
            e?.message || "Failed to place perp order"
          );
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      perpInstrument,
      orderType,
      orderDirection,
      calculateTotalValueWithSize,
      contractPriceStep.amount_precision,
      contractPriceStep.amount_step,
      contractPriceStep.min_order_amount,
      contractPriceStep.price_precision,
      contractPriceStep.price_step,
      getRealContractSize,
      selectedLeverage,
      placePerpOrder,
      closePerpPosition,
      estimatedOpeningFee,
      openingFeeRate,
      addToast,
      addErrorToast,
      handleWrongNetwork,
      tradeUseUSDCTerms,
      reduceOnly,
      currentPosition,
      playSound,
      resetAmountInput,
      tpsl,
      tpslRoeRef,
    ]
  );

  return {
    ...ctx,
    submitOrder,
  };
}
